import {
  METERS_PER_DEGREE_LAT,
  metersPerDegreeLng,
  PolygonShape,
} from 'features/Responder/features/Tours';
import { getZoneLockState } from 'features/Responder/features/Zones';
import { sortZonesForRender } from 'features/Responder/features/Zones';
import L from 'leaflet';
import React, { useEffect, useMemo, useRef, useState } from 'react';

const CAPTURE_LISTENER = true;
const DRAG_START_THRESHOLD_DEGREES = 0.000001;

const toLatLng = (pt) => {
  if (!pt) return null;
  if (Array.isArray(pt) && pt.length >= 2) {
    const lat = Number(pt[0]);
    const lng = Number(pt[1]);
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
  }
  const lat = Number(pt.lat ?? pt.latitude);
  const lng = Number(pt.lng ?? pt.longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

const centroid = (points = []) => {
  const pts = points.map(toLatLng).filter(Boolean);
  if (!pts.length) return null;
  const { lat, lng } = pts.reduce((acc, p) => ({ lat: acc.lat + p.lat, lng: acc.lng + p.lng }), {
    lat: 0,
    lng: 0,
  });
  return { lat: lat / pts.length, lng: lng / pts.length };
};

const vectorMeters = (center, pt) => {
  if (!center || !pt) return { x: 0, y: 0 };
  const lngFactor = metersPerDegreeLng(center.lat || 0) || METERS_PER_DEGREE_LAT;
  return {
    x: (pt.lng - center.lng) * lngFactor,
    y: (pt.lat - center.lat) * METERS_PER_DEGREE_LAT,
    lngFactor,
  };
};

const distanceMeters = (center, pt) => {
  const vec = vectorMeters(center, pt);
  return Math.sqrt(vec.x * vec.x + vec.y * vec.y);
};

const transformPoints = (basePoints, center, scale, angleRad) => {
  if (!center || !Array.isArray(basePoints)) return basePoints;
  if (!Number.isFinite(scale) || !Number.isFinite(angleRad)) return basePoints;
  const lngFactor = metersPerDegreeLng(center.lat || 0) || METERS_PER_DEGREE_LAT;
  const cos = Math.cos(angleRad);
  const sin = Math.sin(angleRad);
  return basePoints.map((pt) => {
    const ll = toLatLng(pt);
    if (!ll) return pt;
    const dx = (ll.lng - center.lng) * lngFactor;
    const dy = (ll.lat - center.lat) * METERS_PER_DEGREE_LAT;
    const rx = dx * cos - dy * sin;
    const ry = dx * sin + dy * cos;
    return {
      lat: center.lat + (ry * scale) / METERS_PER_DEGREE_LAT,
      lng: center.lng + (rx * scale) / lngFactor,
    };
  });
};

export default function ZoneEditLayer({
  zones = [],
  selectedZoneIds = [],
  activeZoneId = null,
  onUpdateZone = () => {},
  onFocusZone = () => {},
  editing = true,
  zoneEditMode = 'select',
  onBeginEdit = () => {},
}) {
  const handleIcon = useMemo(
    () =>
      L.divIcon({
        className: 'checkpoint-handle-icon',
        html: '<div class="checkpoint-handle-dot"></div>',
        iconSize: [16, 16],
        iconAnchor: [8, 8],
      }),
    [],
  );
  const suppressFocusNext = useRef(false);
  const dragOriginRef = useRef(null);
  const mapRef = useRef(null);
  const [isVertexDragging, setIsVertexDragging] = useState(false);
  const pendingDragRef = useRef(null);
  const pendingMoveHandlerRef = useRef(null);
  const pendingUpHandlerRef = useRef(null);
  const resizeMoveHandlerRef = useRef(null);
  const resizeUpHandlerRef = useRef(null);
  const reshapeMoveHandlerRef = useRef(null);
  const reshapeUpHandlerRef = useRef(null);
  const lastFocusedZoneIdRef = useRef(null);
  const clickInfoRef = useRef({ zoneId: null, wasFocused: false });
  const isDraggingRef = useRef(false);
  const editSessionRef = useRef(false);
  const resizeOriginRef = useRef(null);
  const reshapeOriginRef = useRef(null);
  const mapInteractionLockRef = useRef(null);

  const lockMapInteractions = (map) => {
    if (!map) return;
    const existing = mapInteractionLockRef.current;
    if (existing?.locked && existing.map && existing.map !== map) {
      unlockMapInteractions(existing.map);
    }
    if (existing?.locked && existing.map === map) {
      // Already locked for this map; keep it disabled.
      map.dragging?.disable?.();
      map.touchZoom?.disable?.();
      map.doubleClickZoom?.disable?.();
      map.boxZoom?.disable?.();
      map.keyboard?.disable?.();
      map.scrollWheelZoom?.disable?.();
      return;
    }
    mapInteractionLockRef.current = {
      locked: true,
      map,
      dragging: !!map.dragging?.enabled?.(),
      touchZoom: !!map.touchZoom?.enabled?.(),
      doubleClickZoom: !!map.doubleClickZoom?.enabled?.(),
      boxZoom: !!map.boxZoom?.enabled?.(),
      keyboard: !!map.keyboard?.enabled?.(),
      scrollWheelZoom: !!map.scrollWheelZoom?.enabled?.(),
    };
    map.dragging?.disable?.();
    map.touchZoom?.disable?.();
    map.doubleClickZoom?.disable?.();
    map.boxZoom?.disable?.();
    map.keyboard?.disable?.();
    map.scrollWheelZoom?.disable?.();
  };

  const unlockMapInteractions = (map) => {
    const lockState = mapInteractionLockRef.current;
    if (!lockState?.locked) return;
    const targetMap = map || lockState.map;
    if (!targetMap) {
      mapInteractionLockRef.current = null;
      return;
    }
    if (lockState.dragging) targetMap.dragging?.enable?.();
    if (lockState.touchZoom) targetMap.touchZoom?.enable?.();
    if (lockState.doubleClickZoom) targetMap.doubleClickZoom?.enable?.();
    if (lockState.boxZoom) targetMap.boxZoom?.enable?.();
    if (lockState.keyboard) targetMap.keyboard?.enable?.();
    if (lockState.scrollWheelZoom) targetMap.scrollWheelZoom?.enable?.();
    mapInteractionLockRef.current = null;
  };

  const selectedZoneKeys = useMemo(
    () => new Set(selectedZoneIds.map((id) => `${id}`)),
    [selectedZoneIds],
  );
  const zonesForEdit = useMemo(() => {
    if (!Array.isArray(zones)) return [];
    const filtered =
      selectedZoneKeys.size > 0
        ? zones.filter((zone) => selectedZoneKeys.has(`${zone.id}`))
        : zones;
    const narrowed =
      activeZoneId != null ? filtered.filter((z) => `${z.id}` === `${activeZoneId}`) : filtered;
    return sortZonesForRender(narrowed.length ? narrowed : filtered);
  }, [activeZoneId, selectedZoneKeys, zones]);

  useEffect(() => {
    return () => cleanupDrag();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Safety net: if anything leaves a drag disabled, a stray mouseup/click anywhere will clean it up.
  useEffect(() => {
    const handleUp = () => {
      clearPending();
      cleanupDrag();
      resizeOriginRef.current = null;
      setIsVertexDragging(false);
      editSessionRef.current = false;
    };
    window.addEventListener('mouseup', handleUp, CAPTURE_LISTENER);
    return () => {
      window.removeEventListener('mouseup', handleUp, CAPTURE_LISTENER);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearResizeDrag = () => {
    if (resizeMoveHandlerRef.current) {
      window.removeEventListener('mousemove', resizeMoveHandlerRef.current, CAPTURE_LISTENER);
      resizeMoveHandlerRef.current = null;
    }
    if (resizeUpHandlerRef.current) {
      window.removeEventListener('mouseup', resizeUpHandlerRef.current, CAPTURE_LISTENER);
      resizeUpHandlerRef.current = null;
    }
  };

  const clearReshapeDrag = () => {
    if (reshapeMoveHandlerRef.current) {
      window.removeEventListener('mousemove', reshapeMoveHandlerRef.current, CAPTURE_LISTENER);
      reshapeMoveHandlerRef.current = null;
    }
    if (reshapeUpHandlerRef.current) {
      window.removeEventListener('mouseup', reshapeUpHandlerRef.current, CAPTURE_LISTENER);
      reshapeUpHandlerRef.current = null;
    }
  };

  const cleanupDrag = () => {
    const map = mapRef.current || mapInteractionLockRef.current?.map || null;
    const origin = dragOriginRef.current;
    if (map && origin) {
      if (origin.handleDrag) map.off('mousemove', origin.handleDrag);
      if (origin.handleDragDom) {
        window.removeEventListener('mousemove', origin.handleDragDom, CAPTURE_LISTENER);
      }
      if (origin.handleUp) {
        map.off('mouseup', origin.handleUp);
      }
      if (origin.handleUpDom) {
        window.removeEventListener('mouseup', origin.handleUpDom, CAPTURE_LISTENER);
      }
    }
    clearResizeDrag();
    clearReshapeDrag();
    unlockMapInteractions(map);
    dragOriginRef.current = null;
    mapRef.current = null;
    isDraggingRef.current = false;
    editSessionRef.current = false;
    resizeOriginRef.current = null;
    reshapeOriginRef.current = null;
    setIsVertexDragging(false);
  };

  const startZoneDrag = (map, latlng, basePoints, zoneId) => {
    if (!map || !latlng || !Array.isArray(basePoints)) return;
    mapRef.current = map;
    isDraggingRef.current = true;
    const handleDrag = (ev) => {
      const dLat = ev.latlng.lat - latlng.lat;
      const dLng = ev.latlng.lng - latlng.lng;
      const shifted = basePoints.map((p) => {
        const ll = toLatLng(p);
        return ll ? { lat: ll.lat + dLat, lng: ll.lng + dLng } : p;
      });
      onUpdateZone(zoneId, shifted);
    };
    const handleDragDom = (ev) => {
      ev.preventDefault?.();
      ev.stopPropagation?.();
      ev.stopImmediatePropagation?.();
      if (!map) return;
      // Translate the DOM mouse move into latlng using the map
      const ll = map.mouseEventToLatLng(ev);
      if (!ll) return;
      handleDrag({ latlng: ll });
    };
    const handleUp = () => {
      cleanupDrag();
    };
    const handleUpDom = () => {
      cleanupDrag();
    };
    dragOriginRef.current = { handleDrag, handleDragDom, handleUp, handleUpDom };
    lockMapInteractions(map);
    map.on('mousemove', handleDrag);
    map.on('mouseup', handleUp);
    window.addEventListener('mousemove', handleDragDom, CAPTURE_LISTENER);
    window.addEventListener('mouseup', handleUpDom, CAPTURE_LISTENER);
  };

  const clearPending = () => {
    pendingDragRef.current = null;
    if (pendingMoveHandlerRef.current) {
      window.removeEventListener('mousemove', pendingMoveHandlerRef.current, CAPTURE_LISTENER);
      pendingMoveHandlerRef.current = null;
    }
    if (pendingUpHandlerRef.current) {
      window.removeEventListener('mouseup', pendingUpHandlerRef.current, CAPTURE_LISTENER);
      pendingUpHandlerRef.current = null;
    }
  };

  const beginPendingDrag = (map, latlng, basePoints, zoneId) => {
    if (!map || !latlng || !Array.isArray(basePoints)) return;
    pendingDragRef.current = { map, latlng, basePoints, zoneId };
    const handleMove = (ev) => {
      ev.preventDefault?.();
      ev.stopPropagation?.();
      ev.stopImmediatePropagation?.();
      const pending = pendingDragRef.current;
      if (!pending) return;
      const ll = pending.map?.mouseEventToLatLng?.(ev);
      if (!ll) return;
      const dLat = ll.lat - pending.latlng.lat;
      const dLng = ll.lng - pending.latlng.lng;
      const delta = Math.abs(dLat) + Math.abs(dLng);
      if (delta < DRAG_START_THRESHOLD_DEGREES) return;
      const shifted = pending.basePoints.map((point) => {
        const next = toLatLng(point);
        return next ? { lat: next.lat + dLat, lng: next.lng + dLng } : point;
      });
      onUpdateZone(pending.zoneId, shifted);
      clearPending();
      startZoneDrag(pending.map, pending.latlng, pending.basePoints, pending.zoneId);
    };
    const handleUp = () => {
      clearPending();
    };
    pendingMoveHandlerRef.current = handleMove;
    pendingUpHandlerRef.current = handleUp;
    window.addEventListener('mousemove', handleMove, CAPTURE_LISTENER);
    window.addEventListener('mouseup', handleUp, CAPTURE_LISTENER);
  };

  const ensureHistory = (zoneId, points) => {
    if (editSessionRef.current) return;
    if (zoneId == null || !Array.isArray(points)) return;
    editSessionRef.current = true;
    onBeginEdit(zoneId, points);
  };

  return Array.isArray(zonesForEdit)
    ? zonesForEdit.map((zone) => {
        const points =
          (Array.isArray(zone?._points) && zone._points.length ? zone._points : null) ||
          (Array.isArray(zone?.polygon) ? zone.polygon : []);
        const center = centroid(points) || { lat: 0, lng: 0 };
        const fillOpacity = Number.isFinite(zone.fillOpacity) ? zone.fillOpacity : 0.15;
        const fillColor = zone.fillColor || zone.strokeColor || '#f97316';
        const strokeColor = zone.strokeColor || zone.fillColor || '#f97316';
        const cp = {
          id: zone.id,
          geometry: { type: 'polygon', coord: points },
          lat: center.lat,
          lng: center.lng,
        };
        const { locked } = getZoneLockState({ zone });
        const mode = zoneEditMode;
        const isSelectMode = mode === 'select';
        const isReshapeMode = mode === 'reshape';
        const isResizeMode = mode === 'resize';
        const isAddVertexMode = mode === 'addVertex';
        const isEraseVertexMode = mode === 'eraseVertex';
        const allowVertexDrag = false;
        const allowReshapeHandles = editing && !locked && isReshapeMode;
        const allowResizeHandles = editing && !locked && isResizeMode;
        const allowAddVertex = editing && !locked && isAddVertexMode;
        const allowPolygonDrag = editing && !locked && isSelectMode;
        const eraseMode = editing && !locked && isEraseVertexMode;
        const allowFocusOnPolygon = editing && isSelectMode;
        return (
          <React.Fragment key={`zone-edit-${zone.id}`}>
            <PolygonShape
              cp={cp}
              setSelectedCheckpointId={() => {}}
              updateCheckpoint={(id, updates) => {
                if (id !== zone.id) return;
                const pts =
                  updates?.polygon ||
                  updates?.geometry?.coord ||
                  updates?.coord ||
                  updates?.geometry?.points ||
                  updates?.points;
                if (Array.isArray(pts)) {
                  ensureHistory(zone.id, points);
                  if (!isResizeMode) onUpdateZone(zone.id, pts);
                  if (!isVertexDragging && !isDraggingRef.current) {
                    editSessionRef.current = false;
                  }
                }
              }}
              suppressFocusNext={suppressFocusNext}
              handleIcon={handleIcon}
              metersPerDegreeLat={METERS_PER_DEGREE_LAT}
              metersPerDegreeLng={metersPerDegreeLng}
              editing={editing}
              fillColor={fillColor}
              strokeColor={strokeColor}
              fillOpacity={fillOpacity}
              isZone
              onVertexDragStart={(_, event) => {
                const map = event?.target?._map;
                if (map) {
                  mapRef.current = map;
                  lockMapInteractions(map);
                }
                ensureHistory(zone.id, points);
                setIsVertexDragging(true);
              }}
              onVertexDragEnd={(_, event) => {
                const map =
                  event?.target?._map ||
                  mapRef.current ||
                  mapInteractionLockRef.current?.map ||
                  null;
                unlockMapInteractions(map);
                setIsVertexDragging(false);
                editSessionRef.current = false;
                resizeOriginRef.current = null;
              }}
              onVertexResizeStart={(idx, event) => {
                if (!isResizeMode || idx == null) return;
                const map = event?.target?._map;
                const centerPoint = centroid(points);
                const anchor = toLatLng(points[idx]);
                if (!map || !centerPoint || !anchor) return;
                const baseDistance = Math.max(distanceMeters(centerPoint, anchor), 1);
                const baseVector = vectorMeters(centerPoint, anchor);
                const baseAngle = Math.atan2(baseVector.y, baseVector.x);
                const basePoints = points
                  .map((pt) => toLatLng(pt))
                  .filter((pt) => pt && Number.isFinite(pt.lat) && Number.isFinite(pt.lng))
                  .map((pt) => ({ ...pt }));
                if (!basePoints.length) return;
                ensureHistory(zone.id, points);
                setIsVertexDragging(true);
                mapRef.current = map;
                lockMapInteractions(map);
                clearResizeDrag();
                resizeOriginRef.current = {
                  zoneId: zone.id,
                  center: centerPoint,
                  baseDistance,
                  baseAngle,
                  lastAngle: baseAngle,
                  points: basePoints,
                };
                const minDistance = Math.max(baseDistance * 0.08, 1.5);
                const handleMove = (ev) => {
                  ev.preventDefault?.();
                  ev.stopPropagation?.();
                  ev.stopImmediatePropagation?.();
                  const origin = resizeOriginRef.current;
                  if (!origin || origin.zoneId !== zone.id) return;
                  const ll = map.mouseEventToLatLng(ev);
                  if (!ll) return;
                  const nextVector = vectorMeters(origin.center, ll);
                  const rawDistance = Math.sqrt(
                    nextVector.x * nextVector.x + nextVector.y * nextVector.y,
                  );
                  const nextDistance = Math.max(rawDistance, minDistance);
                  const nextScale = nextDistance / origin.baseDistance;
                  const canUpdateAngle = rawDistance > minDistance * 0.75;
                  const nextAngle = canUpdateAngle
                    ? Math.atan2(nextVector.y, nextVector.x)
                    : (origin.lastAngle ?? origin.baseAngle);
                  origin.lastAngle = nextAngle;
                  const nextRotation = nextAngle - origin.baseAngle;
                  const transformed = transformPoints(
                    origin.points,
                    origin.center,
                    nextScale,
                    nextRotation,
                  );
                  onUpdateZone(zone.id, transformed);
                };
                const handleUp = (ev) => {
                  ev?.preventDefault?.();
                  ev?.stopPropagation?.();
                  ev?.stopImmediatePropagation?.();
                  clearResizeDrag();
                  const activeMap =
                    mapRef.current || mapInteractionLockRef.current?.map || map || null;
                  unlockMapInteractions(activeMap);
                  setIsVertexDragging(false);
                  editSessionRef.current = false;
                  resizeOriginRef.current = null;
                };
                resizeMoveHandlerRef.current = handleMove;
                resizeUpHandlerRef.current = handleUp;
                window.addEventListener('mousemove', handleMove, CAPTURE_LISTENER);
                window.addEventListener('mouseup', handleUp, CAPTURE_LISTENER);
              }}
              onVertexReshapeStart={(idx, event) => {
                if (!isReshapeMode || idx == null) return;
                const map = event?.target?._map;
                if (!map) return;
                const basePoints = points
                  .map((pt) => toLatLng(pt))
                  .filter((pt) => pt && Number.isFinite(pt.lat) && Number.isFinite(pt.lng))
                  .map((pt) => ({ ...pt }));
                if (!basePoints.length || !basePoints[idx]) return;
                ensureHistory(zone.id, points);
                setIsVertexDragging(true);
                mapRef.current = map;
                lockMapInteractions(map);
                clearReshapeDrag();
                reshapeOriginRef.current = {
                  zoneId: zone.id,
                  vertexIndex: idx,
                  points: basePoints,
                };
                const handleMove = (ev) => {
                  ev.preventDefault?.();
                  ev.stopPropagation?.();
                  ev.stopImmediatePropagation?.();
                  const origin = reshapeOriginRef.current;
                  if (!origin || origin.zoneId !== zone.id) return;
                  const ll = map.mouseEventToLatLng(ev);
                  if (!ll) return;
                  const next = origin.points.map((pt) => ({ ...pt }));
                  if (!next[origin.vertexIndex]) return;
                  next[origin.vertexIndex] = { lat: ll.lat, lng: ll.lng };
                  onUpdateZone(zone.id, next);
                };
                const handleUp = (ev) => {
                  ev?.preventDefault?.();
                  ev?.stopPropagation?.();
                  ev?.stopImmediatePropagation?.();
                  clearReshapeDrag();
                  const activeMap =
                    mapRef.current || mapInteractionLockRef.current?.map || map || null;
                  unlockMapInteractions(activeMap);
                  setIsVertexDragging(false);
                  editSessionRef.current = false;
                  reshapeOriginRef.current = null;
                };
                reshapeMoveHandlerRef.current = handleMove;
                reshapeUpHandlerRef.current = handleUp;
                window.addEventListener('mousemove', handleMove, CAPTURE_LISTENER);
                window.addEventListener('mouseup', handleUp, CAPTURE_LISTENER);
              }}
              onPolygonMouseDown={(e) => {
                if (!editing) return;
                cleanupDrag();
                clearPending();
                if (e?.originalEvent) {
                  e.originalEvent.preventDefault?.();
                  e.originalEvent.stopPropagation?.();
                  e.originalEvent.stopImmediatePropagation?.();
                  if (e.originalEvent.button !== 0) return;
                }
                const map = e?.target?._map;
                if (allowPolygonDrag && map) {
                  mapRef.current = map;
                  lockMapInteractions(map);
                }
                if (allowPolygonDrag) {
                  ensureHistory(zone.id, points);
                  beginPendingDrag(map, e.latlng, points, zone.id);
                }
                lastFocusedZoneIdRef.current = zone.id;
                clickInfoRef.current = allowFocusOnPolygon
                  ? { zoneId: zone.id, wasFocused: true }
                  : { zoneId: null, wasFocused: false };
              }}
              onPolygonMouseUp={(e) => {
                if (!editing) return;
                if (e?.originalEvent) {
                  e.originalEvent.preventDefault?.();
                  e.originalEvent.stopPropagation?.();
                  e.originalEvent.stopImmediatePropagation?.();
                }
                const map = e?.target?._map;
                clearPending();
                const dragged = isDraggingRef.current;
                cleanupDrag();
                const info = clickInfoRef.current;
                if (allowFocusOnPolygon && !dragged && info.zoneId === zone.id && info.wasFocused) {
                  onFocusZone?.(zone, map);
                }
                clickInfoRef.current = { zoneId: null, wasFocused: false };
              }}
              onPolygonMouseMove={(e) => {
                if (!editing) return;
                if (e?.originalEvent) {
                  e.originalEvent.preventDefault?.();
                  e.originalEvent.stopPropagation?.();
                }
              }}
              disableHoverAdd={!!dragOriginRef.current || !allowAddVertex || eraseMode}
              allowVertexDrag={allowVertexDrag}
              allowReshapeHandles={allowReshapeHandles}
              allowResizeHandles={allowResizeHandles}
              allowAddVertex={allowAddVertex}
              eraseVertexMode={eraseMode}
              onEraseVertex={(idx) => {
                if (!Array.isArray(points) || points.length <= 3) return;
                ensureHistory(zone.id, points);
                const next = points.filter((_, i) => i !== idx);
                if (next.length >= 3) {
                  onUpdateZone(zone.id, next);
                  editSessionRef.current = false;
                }
              }}
            />
          </React.Fragment>
        );
      })
    : null;
}
